/** A JSON cache in sessionStorage, one entry per key under a prefix, so it ends with the tab and clear() empties it on
 *  logout. A full or blocked sessionStorage only costs the cache: nothing here throws. */
export interface SessionCache<T> {
  read(key: string): T | null
  write(key: string, value: T): void
  clear(): void
}

/** `maxChars` skips a value too big to keep: sessionStorage holds about 5 MB for the whole site. */
export function sessionCache<T>(prefix: string, { maxChars = Infinity }: { maxChars?: number } = {}): SessionCache<T> {
  return {
    read(key) {
      try {
        const raw = sessionStorage.getItem(`${prefix}${key}`)
        return raw ? (JSON.parse(raw) as T) : null
      } catch {
        return null
      }
    },
    write(key, value) {
      try {
        const raw = JSON.stringify(value)
        if (raw.length <= maxChars) sessionStorage.setItem(`${prefix}${key}`, raw)
      } catch {
        // Not kept; the next read misses.
      }
    },
    clear() {
      try {
        const keys: string[] = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key?.startsWith(prefix)) keys.push(key)
        }
        for (const key of keys) sessionStorage.removeItem(key)
      } catch {
        // Nothing kept, nothing to clear.
      }
    }
  }
}
